import { mean } from "es-toolkit";

import type { GeoJSONMultiPolygon } from "api/model";

import {
  distanceSq,
  getBoundsFromCoordinates,
  pointAlongSegment,
  type BoundaryPoint,
  type LngLatRing,
  type MapRenderedFeatureQuery,
  type MapViewportProjector,
  type ScreenPoint,
} from "./geo";
import { toRadians } from "./gridMeshGeometry";

export const MIN_BOUNDARY_POINTS_TO_CLOSE = 3;

/** WGS84 authalic radius (m); matches common GeoJSON spherical area helpers. */
const EARTH_RADIUS_METERS = 6_378_137;
const SQUARE_METERS_PER_HECTARE = 10_000;

/**
 * Spherical polygon area in hectares for a closed ring of lng/lat vertices.
 * Returns null when the ring cannot form a polygon.
 */
export const getBoundaryAreaHectares = (
  points: BoundaryPoint[],
): number | null => {
  if (points.length < MIN_BOUNDARY_POINTS_TO_CLOSE) {
    return null;
  }

  // Close the ring for the spherical excess sum (open vertex list in state).
  const coords: [number, number][] = points.map((point) => [
    point.longitude,
    point.latitude,
  ]);
  const first = coords[0];
  if (first == null) {
    return null;
  }
  coords.push(first);

  let area = 0;
  const coordsLength = coords.length;
  for (let i = 0; i < coordsLength; i += 1) {
    let lowerIndex: number;
    let middleIndex: number;
    let upperIndex: number;

    if (i === coordsLength - 2) {
      lowerIndex = coordsLength - 2;
      middleIndex = coordsLength - 1;
      upperIndex = 0;
    } else if (i === coordsLength - 1) {
      lowerIndex = coordsLength - 1;
      middleIndex = 0;
      upperIndex = 1;
    } else {
      lowerIndex = i;
      middleIndex = i + 1;
      upperIndex = i + 2;
    }

    const p1 = coords[lowerIndex];
    const p2 = coords[middleIndex];
    const p3 = coords[upperIndex];
    if (p1 == null || p2 == null || p3 == null) {
      continue;
    }

    area += (toRadians(p3[0]) - toRadians(p1[0])) * Math.sin(toRadians(p2[1]));
  }

  const areaSquareMeters = Math.abs(
    (area * EARTH_RADIUS_METERS * EARTH_RADIUS_METERS) / 2,
  );
  return areaSquareMeters / SQUARE_METERS_PER_HECTARE;
};

const SEGMENT_ORIENTATION_EPSILON = 1e-12;

/** Cross-product orientation: 0 collinear, 1 clockwise, 2 counterclockwise. */
const segmentOrientation = (
  p: BoundaryPoint,
  q: BoundaryPoint,
  r: BoundaryPoint,
): 0 | 1 | 2 => {
  const value =
    (q.latitude - p.latitude) * (r.longitude - q.longitude) -
    (q.longitude - p.longitude) * (r.latitude - q.latitude);
  if (Math.abs(value) <= SEGMENT_ORIENTATION_EPSILON) {
    return 0;
  }
  return value > 0 ? 1 : 2;
};

const isPointOnSegment = (
  segmentStart: BoundaryPoint,
  point: BoundaryPoint,
  segmentEnd: BoundaryPoint,
): boolean =>
  point.longitude <=
    Math.max(segmentStart.longitude, segmentEnd.longitude) +
      SEGMENT_ORIENTATION_EPSILON &&
  point.longitude >=
    Math.min(segmentStart.longitude, segmentEnd.longitude) -
      SEGMENT_ORIENTATION_EPSILON &&
  point.latitude <=
    Math.max(segmentStart.latitude, segmentEnd.latitude) +
      SEGMENT_ORIENTATION_EPSILON &&
  point.latitude >=
    Math.min(segmentStart.latitude, segmentEnd.latitude) -
      SEGMENT_ORIENTATION_EPSILON;

/** True when closed-ring edges (p1–q1) and (p2–q2) properly intersect or overlap. */
const doSegmentsIntersect = (
  p1: BoundaryPoint,
  q1: BoundaryPoint,
  p2: BoundaryPoint,
  q2: BoundaryPoint,
): boolean => {
  const o1 = segmentOrientation(p1, q1, p2);
  const o2 = segmentOrientation(p1, q1, q2);
  const o3 = segmentOrientation(p2, q2, p1);
  const o4 = segmentOrientation(p2, q2, q1);

  // Proper crossing (no collinear endpoints). Collinear touches use on-segment checks.
  if (o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0 && o1 !== o2 && o3 !== o4) {
    return true;
  }

  if (o1 === 0 && isPointOnSegment(p1, p2, q1)) {
    return true;
  }
  if (o2 === 0 && isPointOnSegment(p1, q2, q1)) {
    return true;
  }
  if (o3 === 0 && isPointOnSegment(p2, p1, q2)) {
    return true;
  }
  if (o4 === 0 && isPointOnSegment(p2, q1, q2)) {
    return true;
  }

  return false;
};

/** True when closed-ring edges properly cross; collinear touches are ignored. */
const doSegmentsProperlyCross = (
  p1: BoundaryPoint,
  q1: BoundaryPoint,
  p2: BoundaryPoint,
  q2: BoundaryPoint,
): boolean => {
  const o1 = segmentOrientation(p1, q1, p2);
  const o2 = segmentOrientation(p1, q1, q2);
  const o3 = segmentOrientation(p2, q2, p1);
  const o4 = segmentOrientation(p2, q2, q1);
  return o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0 && o1 !== o2 && o3 !== o4;
};

const isPointOnBoundaryRing = (
  point: BoundaryPoint,
  ring: BoundaryPoint[],
): boolean => {
  const pointCount = ring.length;
  for (let i = 0; i < pointCount; i += 1) {
    const start = ring[i];
    const end = ring[(i + 1) % pointCount];
    if (start == null || end == null) {
      continue;
    }
    if (
      segmentOrientation(start, end, point) === 0 &&
      isPointOnSegment(start, point, end)
    ) {
      return true;
    }
  }
  return false;
};

export const toLngLatRing = (
  points: BoundaryPoint[],
): Array<[number, number]> =>
  points.map((point): [number, number] => [point.longitude, point.latitude]);

/**
 * True when a closed boundary ring crosses or overlaps itself.
 * Adjacent edges that only share a vertex are ignored.
 */
export const isBoundarySelfIntersecting = (
  points: BoundaryPoint[],
): boolean => {
  const pointCount = points.length;
  if (pointCount < MIN_BOUNDARY_POINTS_TO_CLOSE) {
    return false;
  }

  for (let i = 0; i < pointCount; i += 1) {
    const iNext = (i + 1) % pointCount;
    const a = points[i];
    const b = points[iNext];
    if (a == null || b == null) {
      continue;
    }

    for (let j = i + 1; j < pointCount; j += 1) {
      const jNext = (j + 1) % pointCount;
      // Skip edges that share a vertex (including first/last closing pair).
      if (iNext === j || jNext === i) {
        continue;
      }

      const c = points[j];
      const d = points[jNext];
      if (c == null || d == null) {
        continue;
      }

      if (doSegmentsIntersect(a, b, c, d)) {
        return true;
      }
    }
  }

  return false;
};

/** Pixel hit radius for vertex drag/remove; keep near rendered circle radius (5–8px). */
export const BOUNDARY_VERTEX_HIT_PIXELS = 10;

/**
 * Padding around the click for line hit-testing. The stroke is only ~2px, and the
 * cursor hotspot can sit a few pixels off the visible tip.
 */
export const BOUNDARY_LINE_HIT_PIXELS = 10;

/** Nearest vertex to a screen point after projecting lng/lat through the map. */
export const findNearestVertex = (
  map: MapViewportProjector,
  points: BoundaryPoint[],
  screenPoint: ScreenPoint,
): { distancePx: number; index: number } | null => {
  let best: { distancePx: number; index: number } | null = null;

  points.forEach((point, index) => {
    const projected = map.project([point.longitude, point.latitude]);
    const distancePx = Math.hypot(
      projected.x - screenPoint.x,
      projected.y - screenPoint.y,
    );
    if (best == null || distancePx < best.distancePx) {
      best = { distancePx, index };
    }
  });

  return best;
};

/** Index of the nearest vertex within `hitPixels`, or null if none are close enough. */
export const findVertexIndexAtScreenPoint = (
  map: MapViewportProjector,
  points: BoundaryPoint[],
  screenPoint: ScreenPoint,
  hitPixels: number = BOUNDARY_VERTEX_HIT_PIXELS,
): number | null => {
  const nearest = findNearestVertex(map, points, screenPoint);
  if (nearest == null || nearest.distancePx > hitPixels) {
    return null;
  }

  return nearest.index;
};

export const hasLayerFeaturesNearScreenPoint = (
  map: MapRenderedFeatureQuery,
  layerId: string,
  screenPoint: ScreenPoint,
  hitPixels: number,
): boolean => {
  if (map.getLayer(layerId) == null) {
    return false;
  }

  const features = map.queryRenderedFeatures(
    [
      [screenPoint.x - hitPixels, screenPoint.y - hitPixels],
      [screenPoint.x + hitPixels, screenPoint.y + hitPixels],
    ],
    { layers: [layerId] },
  );

  return features.length > 0;
};

/**
 * Closest point on the finite segment start→end (parametric projection along
 * the segment, clamped to [0, 1]), plus squared distance from `point` to that
 * result in lng/lat space.
 */
const getClosestPointOnSegment = (
  point: BoundaryPoint,
  start: BoundaryPoint,
  end: BoundaryPoint,
): { distanceSq: number; point: BoundaryPoint } => {
  const deltaLongitude = end.longitude - start.longitude;
  const deltaLatitude = end.latitude - start.latitude;
  const segmentLengthSq = distanceSq(start, end);

  if (segmentLengthSq === 0) {
    return {
      distanceSq: distanceSq(point, start),
      point: start,
    };
  }

  // 1) Parametric projection onto the infinite line through start→end.
  const projection =
    ((point.longitude - start.longitude) * deltaLongitude +
      (point.latitude - start.latitude) * deltaLatitude) /
    segmentLengthSq;
  // 2) Clamp to the segment, then interpolate.
  const clampedProjection = Math.max(0, Math.min(1, projection));
  const closestPoint = {
    latitude: start.latitude + clampedProjection * deltaLatitude,
    longitude: start.longitude + clampedProjection * deltaLongitude,
  };

  return {
    distanceSq: distanceSq(point, closestPoint),
    point: closestPoint,
  };
};

/**
 * Ray-casting point-in-polygon test for a simple ring (e.g. GeoTIFF image
 * corners). The ring may be open or closed.
 *
 * Strategy (even-odd rule): cast a horizontal ray from the point to +∞ in
 * longitude and count crossings of ring edges. Each crossing toggles
 * inside/outside; an odd final count means the point is inside. An edge
 * counts only when it straddles the point's latitude and the crossing's
 * longitude is strictly to the right of the point.
 */
export const isPointInPolygon = (
  point: BoundaryPoint,
  ring: LngLatRing,
): boolean => {
  if (ring.length < 3) {
    return false;
  }

  const x = point.longitude;
  const y = point.latitude;
  let isInside = false;

  for (
    let index = 0, previousIndex = ring.length - 1;
    index < ring.length;
    previousIndex = index, index += 1
  ) {
    const current = ring[index];
    const previous = ring[previousIndex];
    if (current == null || previous == null) {
      continue;
    }

    const [currentX, currentY] = current;
    const [previousX, previousY] = previous;
    // Toggle when a ring edge straddles y and the crossing lies to the right of x.
    const crossesVertical =
      currentY > y !== previousY > y &&
      x <
        ((previousX - currentX) * (y - currentY)) / (previousY - currentY) +
          currentX;
    if (crossesVertical) {
      isInside = !isInside;
    }
  }

  return isInside;
};

/** True when the point is inside the ring or lies on an edge. */
export const isPointInOrOnPolygon = (
  point: BoundaryPoint,
  ring: LngLatRing,
): boolean => {
  if (isPointInPolygon(point, ring)) {
    return true;
  }

  const points = ring.map(([longitude, latitude]) => ({
    latitude,
    longitude,
  }));
  return isPointOnBoundaryRing(point, points);
};

/**
 * True when `inner` is not fully inside `outer`. Vertices on the outer
 * boundary are allowed; a proper edge crossing counts as outside.
 */
export const doesRingExtendOutsideRing = (
  inner: LngLatRing,
  outer: LngLatRing,
): boolean => {
  const innerPoints = inner.map(([longitude, latitude]) => ({
    latitude,
    longitude,
  }));
  const outerPoints = outer.map(([longitude, latitude]) => ({
    latitude,
    longitude,
  }));
  if (
    innerPoints.length < MIN_BOUNDARY_POINTS_TO_CLOSE ||
    outerPoints.length < MIN_BOUNDARY_POINTS_TO_CLOSE
  ) {
    return false;
  }

  if (innerPoints.some((point) => !isPointInOrOnPolygon(point, outer))) {
    return true;
  }

  const innerCount = innerPoints.length;
  const outerCount = outerPoints.length;
  for (let innerIndex = 0; innerIndex < innerCount; innerIndex += 1) {
    const innerStart = innerPoints[innerIndex];
    const innerEnd = innerPoints[(innerIndex + 1) % innerCount];
    if (innerStart == null || innerEnd == null) {
      continue;
    }

    for (let outerIndex = 0; outerIndex < outerCount; outerIndex += 1) {
      const outerStart = outerPoints[outerIndex];
      const outerEnd = outerPoints[(outerIndex + 1) % outerCount];
      if (outerStart == null || outerEnd == null) {
        continue;
      }
      if (doSegmentsProperlyCross(innerStart, innerEnd, outerStart, outerEnd)) {
        return true;
      }
    }
  }

  return false;
};

const EDGE_INTERIOR_SAMPLE_FRACTIONS = [0.25, 0.5, 0.75] as const;

/**
 * True when a sample along an open edge of `points` lies strictly inside `otherRing`.
 *
 * Shifted rectangles overlap this way: both endpoints of one edge sit on the
 * other boundary, so the edges do not make an X crossing and no vertex lies
 * strictly inside, while the edge itself runs through the interior. Samples at
 * 1/4, 1/2, and 3/4 of each edge catch that. A sample on a shared edge lies on
 * the boundary and is ignored.
 */
const hasOpenEdgePointStrictlyInside = (
  points: BoundaryPoint[],
  otherPoints: BoundaryPoint[],
  otherRing: LngLatRing,
): boolean => {
  for (let index = 0; index < points.length; index += 1) {
    const start = points[index];
    const end = points[(index + 1) % points.length];
    if (start == null || end == null) {
      continue;
    }

    for (const fraction of EDGE_INTERIOR_SAMPLE_FRACTIONS) {
      const point = pointAlongSegment(start, end, fraction);
      if (
        !isPointOnBoundaryRing(point, otherPoints) &&
        isPointInPolygon(point, otherRing)
      ) {
        return true;
      }
    }
  }

  return false;
};

/**
 * True when two closed rings' interiors intersect. Shared edges/vertices do not
 * count (adjacent sites may touch).
 */
const doesBoundaryOverlap = (
  points: BoundaryPoint[],
  otherPoints: BoundaryPoint[],
): boolean => {
  // Open paths and degenerate rings cannot enclose an interior.
  if (
    points.length < MIN_BOUNDARY_POINTS_TO_CLOSE ||
    otherPoints.length < MIN_BOUNDARY_POINTS_TO_CLOSE
  ) {
    return false;
  }

  const pointCount = points.length;
  const otherCount = otherPoints.length;
  // 1) Proper edge crossings (X intersections). Collinear shared edges are ignored.
  for (let i = 0; i < pointCount; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % pointCount];
    if (a == null || b == null) {
      continue;
    }

    for (let j = 0; j < otherCount; j += 1) {
      const c = otherPoints[j];
      const d = otherPoints[(j + 1) % otherCount];
      if (c == null || d == null) {
        continue;
      }
      if (doSegmentsProperlyCross(a, b, c, d)) {
        return true;
      }
    }
  }

  // 2) Containment: a vertex of this ring strictly inside the other (not on its boundary).
  const otherRing = toLngLatRing(otherPoints);
  if (
    points.some(
      (point) =>
        !isPointOnBoundaryRing(point, otherPoints) &&
        isPointInPolygon(point, otherRing),
    )
  ) {
    return true;
  }

  // 3) The other way: a vertex of the other ring strictly inside this one.
  const ring = toLngLatRing(points);
  if (
    otherPoints.some(
      (point) =>
        !isPointOnBoundaryRing(point, points) && isPointInPolygon(point, ring),
    )
  ) {
    return true;
  }

  // 4) Edge through the other interior with both endpoints on its boundary.
  return (
    hasOpenEdgePointStrictlyInside(points, otherPoints, otherRing) ||
    hasOpenEdgePointStrictlyInside(otherPoints, points, ring)
  );
};

/**
 * True when a closed ring's interior intersects any other closed ring.
 * Shared edges and vertices (adjacent polygons) do not count.
 */
export const doesBoundaryOverlapAny = (
  points: BoundaryPoint[],
  otherRings: BoundaryPoint[][],
): boolean =>
  otherRings.some((otherPoints) => doesBoundaryOverlap(points, otherPoints));

/**
 * Returns `point` when inside `ring`; otherwise the nearest point on the ring
 * boundary (for clamping drags/clicks to a containing polygon).
 */
export const clampPointToPolygon = (
  point: BoundaryPoint,
  ring: LngLatRing,
): BoundaryPoint => {
  if (ring.length < 3 || isPointInPolygon(point, ring)) {
    return point;
  }

  // Outside: snap to the nearest point on any ring edge.
  let bestDistanceSq = Number.POSITIVE_INFINITY;
  let bestPoint = point;

  for (let index = 0; index < ring.length; index += 1) {
    const startLngLat = ring[index];
    const endLngLat = ring[(index + 1) % ring.length];
    if (startLngLat == null || endLngLat == null) {
      continue;
    }

    const closest = getClosestPointOnSegment(
      point,
      { latitude: startLngLat[1], longitude: startLngLat[0] },
      { latitude: endLngLat[1], longitude: endLngLat[0] },
    );
    if (closest.distanceSq < bestDistanceSq) {
      bestDistanceSq = closest.distanceSq;
      bestPoint = closest.point;
    }
  }

  return bestPoint;
};

/**
 * Finds where to insert a vertex on the nearest boundary edge.
 * `insertIndex` is the index the new point should occupy in the points array.
 */
export const findBoundaryEdgeInsertion = (
  points: BoundaryPoint[],
  click: BoundaryPoint,
  isClosed: boolean,
): { insertIndex: number; point: BoundaryPoint } | null => {
  if (points.length < 2) {
    return null;
  }

  const segmentCount = isClosed ? points.length : points.length - 1;
  let bestDistanceSq = Number.POSITIVE_INFINITY;
  let bestInsertIndex = 1;
  let bestPoint = click;

  // Pick the nearest segment (including the closing edge when closed).
  for (let index = 0; index < segmentCount; index += 1) {
    const start = points[index];
    const end = points[(index + 1) % points.length];
    if (start == null || end == null) {
      continue;
    }

    const closest = getClosestPointOnSegment(click, start, end);
    if (closest.distanceSq < bestDistanceSq) {
      bestDistanceSq = closest.distanceSq;
      bestInsertIndex = index + 1;
      bestPoint = closest.point;
    }
  }

  return {
    insertIndex: bestInsertIndex,
    point: bestPoint,
  };
};

export const toBoundaryGeoJson = (
  points: BoundaryPoint[],
  isClosed: boolean,
  label = "",
) => {
  const pathCoordinates = points.map(
    (point) => [point.longitude, point.latitude] as [number, number],
  );
  const trimmedLabel = label.trim();

  const features: Array<{
    type: "Feature";
    geometry:
      | {
          type: "LineString";
          coordinates: Array<[number, number]>;
        }
      | {
          type: "Polygon";
          coordinates: Array<Array<[number, number]>>;
        }
      | {
          type: "Point";
          coordinates: [number, number];
        };
    properties: {
      index?: number;
      kind: "label" | "line" | "polygon" | "vertex";
      label?: string;
      role?: "first" | "vertex";
    };
  }> = [];

  if (isClosed && pathCoordinates.length >= MIN_BOUNDARY_POINTS_TO_CLOSE) {
    const closedRing = [...pathCoordinates, pathCoordinates[0]];
    features.push({
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [closedRing],
      },
      properties: {
        kind: "polygon",
      },
    });

    if (trimmedLabel !== "") {
      features.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: closedRing,
        },
        properties: {
          kind: "label",
          label: trimmedLabel,
        },
      });
    }
  } else if (pathCoordinates.length >= 2) {
    features.push({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: pathCoordinates,
      },
      properties: {
        kind: "line",
      },
    });
  }

  points.forEach((point, index) => {
    features.push({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [point.longitude, point.latitude],
      },
      properties: {
        index,
        kind: "vertex",
        role: index === 0 ? "first" : "vertex",
      },
    });
  });

  return {
    type: "FeatureCollection" as const,
    features,
  };
};

/** Closed boundary ring as API MultiPolygon (exterior ring only). */
export const toBoundaryMultiPolygon = (
  points: BoundaryPoint[],
): GeoJSONMultiPolygon | null => {
  if (points.length < MIN_BOUNDARY_POINTS_TO_CLOSE) {
    return null;
  }

  const ring = points.map(
    (point) => [point.longitude, point.latitude] as [number, number],
  );
  const first = ring[0];
  if (first == null) {
    return null;
  }

  return {
    type: "MultiPolygon",
    coordinates: [[[...ring, first]]],
  };
};

/** True when a MultiPolygon has at least one polygon. */
export const hasBoundaryGeoJson = (
  boundary: GeoJSONMultiPolygon | null | undefined,
): boolean => (boundary?.coordinates.length ?? 0) > 0;

/** Exterior ring of the first polygon → open vertex list (drops closing duplicate). */
export const fromBoundaryMultiPolygon = (
  boundary: GeoJSONMultiPolygon,
): BoundaryPoint[] | null => {
  const ring = boundary.coordinates[0]?.[0];
  if (ring == null || ring.length < MIN_BOUNDARY_POINTS_TO_CLOSE) {
    return null;
  }

  const first = ring[0];
  const last = ring[ring.length - 1];
  const isClosedRing =
    first != null &&
    last != null &&
    first[0] === last[0] &&
    first[1] === last[1];
  const openRing = isClosedRing ? ring.slice(0, -1) : ring;

  if (openRing.length < MIN_BOUNDARY_POINTS_TO_CLOSE) {
    return null;
  }

  return openRing.map(([longitude, latitude]) => ({
    latitude,
    longitude,
  }));
};

/** Bbox center of a MultiPolygon boundary, or null when the ring is unusable. */
export const getBoundaryCenter = (
  boundary: GeoJSONMultiPolygon | null | undefined,
): BoundaryPoint | null => {
  if (boundary == null || !hasBoundaryGeoJson(boundary)) {
    return null;
  }

  const points = fromBoundaryMultiPolygon(boundary);
  if (points == null) {
    return null;
  }

  const bounds = getBoundsFromCoordinates(points);
  if (bounds == null) {
    return null;
  }

  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  return {
    latitude: (minLat + maxLat) / 2,
    longitude: (minLng + maxLng) / 2,
  };
};

/** Mean of each usable boundary's bbox center, or null when none can be used. */
export const getMeanBoundaryCenter = (
  boundaries: Array<GeoJSONMultiPolygon | null | undefined>,
): BoundaryPoint | null => {
  const centers = boundaries
    .map((boundary) => getBoundaryCenter(boundary))
    .filter((center): center is BoundaryPoint => center != null);
  if (centers.length === 0) {
    return null;
  }

  return {
    latitude: mean(centers.map((center) => center.latitude)),
    longitude: mean(centers.map((center) => center.longitude)),
  };
};

/** Saved MultiPolygons as outline features for the map overlay. */
export const toBoundariesGeoJson = (
  items: Array<{
    boundary?: GeoJSONMultiPolygon | null;
    /** Drives the `dimmed` feature property so layers can fade this boundary. */
    isDimmed?: boolean;
    label?: string | null;
    id?: number | string;
  }>,
) => {
  const features = items.flatMap((item) => {
    if (item.boundary == null || !hasBoundaryGeoJson(item.boundary)) {
      return [];
    }

    const nextFeatures: Array<{
      type: "Feature";
      geometry:
        | GeoJSONMultiPolygon
        | {
            type: "LineString";
            coordinates: Array<[number, number]>;
          };
      properties: {
        dimmed: boolean;
        kind: "label" | "polygon";
        label?: string;
        id?: number | string;
      };
    }> = [
      {
        type: "Feature",
        geometry: item.boundary,
        properties: {
          dimmed: item.isDimmed ?? false,
          kind: "polygon",
          ...(item.id != null ? { id: item.id } : {}),
        },
      },
    ];

    const trimmedLabel = item.label?.trim() ?? "";
    if (trimmedLabel === "") {
      return nextFeatures;
    }

    item.boundary.coordinates.forEach((polygon) => {
      const points = fromBoundaryMultiPolygon({
        coordinates: [polygon],
        type: "MultiPolygon",
      });
      if (points == null) {
        return;
      }

      const labelFeature = toBoundaryGeoJson(
        points,
        true,
        trimmedLabel,
      ).features.find((feature) => feature.properties.kind === "label");
      if (labelFeature == null || labelFeature.geometry.type !== "LineString") {
        return;
      }

      nextFeatures.push({
        type: "Feature",
        geometry: labelFeature.geometry,
        properties: {
          dimmed: item.isDimmed ?? false,
          kind: "label",
          label: trimmedLabel,
        },
      });
    });

    return nextFeatures;
  });

  return {
    type: "FeatureCollection" as const,
    features,
  };
};
